import React, { createContext, useEffect, useRef, useState } from 'react'
import './desktop.css'
import AppObj from 'model/appConfigObj';
import TaskObj, { TaskInitParams } from 'model/taskObj';
import Taskbar from './taskbar';
import DemoApp from 'screen/apps/demoApp';
import FileBrowser from 'screen/apps/fileBrowser';
import FileBrowserSettings from 'screen/apps/fileBrowserSettings';
import FileUploader from 'screen/apps/fileUploader';
import ImageViewer from 'screen/apps/imageViewer';
import VideoPlayer from 'screen/apps/videoPlayer';
import AudioPlayer from 'screen/apps/audioPlayer';
import PdfViewer from 'screen/apps/pdfViewer';
import UnsupportedFile from 'screen/apps/unsupportedFile';
import Window from './window';
import Splash from 'screen/splash';
import FileBrowserLaunchFx from './fileBrowserLaunchFx';
import localStorageService from 'service/localStorageService';
import { useNavigate } from "react-router-dom"
import apiService from 'service/apiService';
import { startFileDownload, startZipDownload } from 'service/fileDownload';
import { runItAfterYouDoneTalking, uuidv4 } from 'screen/util/commonUtil';
import { SessionUser, withAppPreference } from 'model/userSession';
import {
  WEB_CHECK_MS,
  WEB_RENEW_REMAINING_MS,
  MOBILE_RENEW_REMAINING_MS,
  isDocumentHidden,
  isIdleExpired,
  isMobileClient,
  isSessionInUse,
  markSessionUsed,
} from 'service/sessionActivity';
import { listenForShareLaunch, takeShareInbox } from 'service/shareInbox';
import { stashUploadDrop } from 'screen/apps/fileUploader/api';
import { tokenObject } from 'model/tokenObj';
import { AxiosError } from 'axios';

export const desktopAppTaskContex = createContext<any>(null);


export default function Desktop() {
  const navigate = useNavigate();
  const [splashScreen, setSplashScreen] = useState(true);
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(localStorageService.getSessionUser());
  const [wallpaperUrl, setWallpaperUrl] = useState<string | null>(null);
  const [appList, setAppList] = useState<AppObj[]>([
    DemoApp,
    FileBrowser,
    FileBrowserSettings,
    FileUploader,
    ImageViewer,
    VideoPlayer,
    AudioPlayer,
    PdfViewer,
    UnsupportedFile,
  ]);
  const [taskList, setTaskList] = useState<TaskObj[]>([]);
  const [launchFX, setLaunchFX] = useState(false);
  const [screenHeight, setScreenHeight] = useState<number>(0)
  const [screenWidth, setScreenWidth] = useState<number>(0)
  const prefSaveTimerRef = useRef(0);
  const wallpaperUrlRef = useRef<string | null>(null);
  const shareBusyRef = useRef(false);
  const desktopReadyRef = useRef(false);
  const openShareRef = useRef<(files: File[]) => boolean>(() => false);

  const expireSession = async (message?: string) => {
    try {
      await apiService.logout();
    } catch {
      /* still clear local session */
    }
    localStorageService.clearTokens();
    if (message) {
      alert(message);
    }
    navigate('/gate');
  };

  const authControll = {
    renewTheToken: async () => {
      try {
        await apiService.renewSession();
        setSessionUser(localStorageService.getSessionUser());
      } catch (error) {
        // apiClient only clears the token after a definitive auth rejection.
        // A phone can briefly have no network while returning foreground;
        // that must not turn into a logout.
        if (!localStorageService.hasToken()) {
          navigate('/gate');
          return;
        }
        console.warn('Session renew deferred', error);
      }
    }
  };

  useEffect(() => {
    void checkTokenIsValidOnStart();
    const splashFailsafe = window.setTimeout(() => setSplashScreen(false), 12000);
    readAndUpdateDisplaySize();
    window.addEventListener("resize", readAndUpdateDisplaySize);
    const onUsed = () => runItAfterYouDoneTalking(markSessionUsed);
    window.addEventListener("mousemove", onUsed);
    window.addEventListener("keydown", onUsed);
    window.addEventListener("click", onUsed);
    window.addEventListener("scroll", onUsed, true);
    window.addEventListener("touchstart", onUsed);
    const interval = window.setInterval(() => {
      void keepTokenNotExpired();
    }, WEB_CHECK_MS);
    const onForeground = () => {
      if (isDocumentHidden()) {
        return;
      }
      markSessionUsed();
      // During a fresh page load, checkTokenIsValidOnStart is using the current
      // token. Rotating it here at the same time makes that request fail with
      // 401 and used to clear a valid login.
      if (desktopReadyRef.current) {
        void keepTokenNotExpired({ forceRenew: isMobileClient() });
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        onForeground();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onForeground);
    const onSharePageshow = () => {
      if (desktopReadyRef.current) {
        void tryLaunchShareUpload();
      }
    };
    window.addEventListener('pageshow', onSharePageshow);
    const unlistenShareLaunch = listenForShareLaunch(() => {
      if (desktopReadyRef.current) {
        void tryLaunchShareUpload();
      }
    });
    const onWallpaper = () => { void loadWallpaper(); };
    window.addEventListener('argon-wallpaper-changed', onWallpaper);
    return () => {
      window.removeEventListener("resize", readAndUpdateDisplaySize);
      window.removeEventListener("mousemove", onUsed);
      window.removeEventListener("keydown", onUsed);
      window.removeEventListener("click", onUsed);
      window.removeEventListener("scroll", onUsed, true);
      window.removeEventListener("touchstart", onUsed);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onForeground);
      window.removeEventListener('pageshow', onSharePageshow);
      unlistenShareLaunch();
      window.removeEventListener('argon-wallpaper-changed', onWallpaper);
      window.clearInterval(interval);
      window.clearTimeout(splashFailsafe);
      if (wallpaperUrlRef.current) {
        URL.revokeObjectURL(wallpaperUrlRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleApps = appList.filter((a) => a.config.onList !== false);

  const desktopControll = {
    screenSize: {
      screenWidth: screenWidth,
      screenHeight: screenHeight
    },
    appList: visibleApps,
    setAppList: setAppList,
    taskList: taskList,
    setTaskList: setTaskList,
    sessionUser,
    setSessionUser,
    saveUserPreference: (appId: string, preference: Record<string, unknown>) => {
      setSessionUser((prev) => {
        const next = withAppPreference(prev, appId, preference);
        if (next) {
          localStorageService.saveSessionUser(next);
          return next;
        }
        return prev;
      });
      if (prefSaveTimerRef.current) {
        window.clearTimeout(prefSaveTimerRef.current);
      }
      prefSaveTimerRef.current = window.setTimeout(() => {
        const prefs = localStorageService.getSessionUser()?.userProfile?.userAppPreferences;
        if (!prefs) {
          return;
        }
        void apiService.saveUserAppPreferences(prefs).then((res) => {
          if (!res.data?.success || !res.data.data) {
            return;
          }
          setSessionUser((prev) => {
            if (!prev) {
              return prev;
            }
            const next = {
              ...prev,
              userProfile: {
                ...(prev.userProfile || {}),
                userAppPreferences: res.data.data,
              },
              profileLoaded: true,
            };
            localStorageService.saveSessionUser(next);
            return next;
          });
        }).catch(() => {
          /* keep optimistic session */
        });
      }, 450);
    },
    setTaskTitle: (taskID: string, taskTitle: string) => {
      setTaskList((pre) => {
        const task = pre.find((t) => t.taskID === taskID);
        if (!task || task.taskTitle === taskTitle) {
          return pre;
        }
        task.taskTitle = taskTitle;
        return [...pre];
      });
    },
    setTaskClosable: (taskID: string, closable: boolean) => {
      setTaskList((pre) => {
        const task = pre.find((t) => t.taskID === taskID);
        if (!task || task.closable === closable) {
          return pre;
        }
        task.closable = closable;
        return [...pre];
      });
    },
    openingTask: (appID: string, taskInitParms?: TaskInitParams[], taskTitle?: string) => {
      let selectingApp = appList.find((appFind) => (appFind.config.appID === appID));

      if (selectingApp) {
        const selectedApp: AppObj = selectingApp
        let newID = selectedApp.config.appID + '_' + uuidv4();
        const newTask = new TaskObj();
        newTask.app = selectedApp;
        newTask.taskTitle = taskTitle || '';
        newTask.taskID = newID;
        newTask.state = "focus";
        newTask.closable = selectedApp.config.defaultClosable !== false;
        newTask.taskInitParms = taskInitParms ? taskInitParms : [];

        const findingTask = taskList.filter(
          (task: TaskObj) =>
            task.app?.config.appID === newTask.app?.config.appID && task.state !== 'killing'
        )

        if (findingTask.length < newTask.app.config.maxHead) {
          setTaskList((pre: TaskObj[]) => {
            pre.forEach((taskss) => { if (taskss.state === "focus") { taskss.state = "unfocus" } })
            newTask.zindex = findMaxZindex(pre) + 1;
            return (([...pre, newTask]))
          });
          return true;
        } else {
          alert('This app reched the max task head limit.')
          return false;
        }
      } else {
        alert('Error on openning app')
        return false;
      }
    },
    /** Focus an existing window of this app and replace its init params; otherwise open a new one. */
    openOrReuseTask: (appID: string, taskInitParms?: TaskInitParams[], taskTitle?: string) => {
      const selectingApp = appList.find((appFind) => appFind.config.appID === appID);
      if (!selectingApp) {
        alert('Error on openning app');
        return;
      }
      setTaskList((pre: TaskObj[]) => {
        const existing = pre.find(
          (task) => task.app?.config.appID === appID && task.state !== 'killing'
        );
        if (existing) {
          existing.taskInitParms = taskInitParms ? [...taskInitParms] : [];
          if (taskTitle) {
            existing.taskTitle = taskTitle;
          }
          const maxZ = findMaxZindex(pre);
          pre.forEach((taskss) => {
            if (taskss.state === 'focus') {
              taskss.state = 'unfocus';
            }
          });
          existing.state = 'focus';
          existing.zindex = maxZ + 1;
          return [...pre];
        }
        const liveCount = pre.filter(
          (task) => task.app?.config.appID === appID && task.state !== 'killing'
        ).length;
        if (liveCount >= selectingApp.config.maxHead) {
          alert('This app reched the max task head limit.');
          return pre;
        }
        const maxZ = findMaxZindex(pre);
        const newTask = new TaskObj();
        newTask.zindex = maxZ + 1;
        newTask.app = selectingApp;
        newTask.taskTitle = taskTitle || '';
        newTask.taskID = selectingApp.config.appID + '_' + uuidv4();
        newTask.state = 'focus';
        newTask.closable = selectingApp.config.defaultClosable !== false;
        newTask.taskInitParms = taskInitParms ? taskInitParms : [];
        pre.forEach((taskss) => {
          if (taskss.state === 'focus') {
            taskss.state = 'unfocus';
          }
        });
        return [...pre, newTask];
      });
    },
    launchFileBrowser: () => {
      desktopControll.openingTask('fileBrowser');
      setLaunchFX(true);
    },
    openFileBrowserSettings: () => {
      const existing = taskList.find(
        (t) => t.app?.config.appID === 'fileBrowserSettings' && t.state !== 'killing'
      );
      if (existing) {
        desktopControll.focusTask(existing.taskID);
        return;
      }
      desktopControll.openingTask('fileBrowserSettings');
    },
    launchFileDownload: (path: string) => {
      if (!path) {
        return;
      }
      void startFileDownload(path).catch((e: any) => {
        alert(e?.message || 'Download failed');
      });
    },
    launchZipDownload: (paths: string[]) => {
      if (!paths || paths.length === 0) {
        return;
      }
      void startZipDownload(paths).catch((e: any) => {
        alert(e?.message || 'Download failed');
      });
    },
    killTask: (taskID: string) => {
      setTaskList(pre => {
        let selectingTask = pre.find((taskFind) => (taskFind.taskID === taskID));
        if (selectingTask && selectingTask.closable === false) {
          return pre;
        }
        let selectingTaskIndex = pre.findIndex((taskFind) => (taskFind.taskID === taskID));
        if (selectingTaskIndex > -1) {
          pre[selectingTaskIndex].state = "killing"
        }
        return ([...pre])
      })
      setTimeout(() => {
        setTaskList(pre => {
          let selectingTask = pre.find((taskFind) => (taskFind.taskID === taskID));
          if (selectingTask && selectingTask.closable === false) {
            return pre;
          }
          let selectingTaskIndex = pre.findIndex((taskFind) => (taskFind.taskID === taskID));
          if (selectingTaskIndex > -1) {
            pre.splice(selectingTaskIndex, 1);
          }
          return ([...pre])
        })
      }, 200);
    },
    focusTask: (taskID: string) => {
      setTaskList((pre) => {
        if (!taskID) {
          pre.forEach((taskss) => {
            if (taskss.state === 'focus') { taskss.state = 'unfocus' }
          })
          return ([...pre])
        }
        let selectingTask = pre.find((taskFind) => taskFind.taskID === taskID);
        if (!selectingTask || selectingTask.state === 'killing') {
          return pre;
        }
        const maxZ = findMaxZindex(pre);
        const alreadyTop = selectingTask.state === 'focus' && selectingTask.zindex >= maxZ;
        if (alreadyTop) {
          return pre;
        }
        pre.forEach((taskss) => {
          if (taskss.state === 'focus') {
            taskss.state = 'unfocus'
          }
        })
        selectingTask.state = 'focus'
        selectingTask.zindex = maxZ + 1
        return ([...pre])
      })
    },
    minisizedTask: (taskID: string) => {
      setTaskList(current => {
        if (taskID === "*") {
          current.forEach((loop) => {
            if (loop.closable !== false) {
              loop.state = 'minimized'
            }
          })
        } else {
          let selectingTaskIndex = current.findIndex((taskFind) => (taskFind.taskID === taskID));
          if (selectingTaskIndex > -1) {
            current[selectingTaskIndex].state = 'minimized'
          }
        }
        return ([...current])
      })
    },
    spotlightTask: (taskID: string) => {
      setTaskList((current) => {
        if (!taskID || taskID === '') {
          current.forEach((taskss) => {
            taskss.spotlighted = false
          })
          return ([...current])
        }
        let selectingTask = current.find((taskFind) => (taskFind.taskID === taskID));
        if (selectingTask) {
          current.forEach((taskss) => {
            taskss.spotlighted = false;
          })
          selectingTask.spotlighted = true
        }
        return ([...current])
      })
    }
  }

  openShareRef.current = (files: File[]) => {
    const session = stashUploadDrop({
      destPath: '',
      existingPaths: [],
      entries: [],
      files,
    });
    const opened = desktopControll.openingTask(
      'fileUploader',
      [{ command: 'session', value: session }],
      'Share'
    );
    if (opened === false) {
      return false;
    }
    return true;
  };

  const keepTokenNotExpired = async (opts?: { forceRenew?: boolean }) => {
    if (isDocumentHidden()) {
      return;
    }
    if (!isMobileClient() && isIdleExpired()) {
      await expireSession("Session timeout.");
      return;
    }
    const raw = localStorageService.getSessionToken().getFullString();
    if (!raw) {
      return;
    }
    try {
      const remaining = localStorageService.getSessionToken().getRemainingMs();
      if (remaining <= 0) {
        await expireSession("Session timeout.");
        return;
      }
      const renewAt = isMobileClient() ? MOBILE_RENEW_REMAINING_MS : WEB_RENEW_REMAINING_MS;
      const due = opts?.forceRenew || remaining < renewAt;
      if (due && (isMobileClient() || isSessionInUse())) {
        await authControll.renewTheToken();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadWallpaper = async () => {
    let next: string | null = null;
    try {
      const blob = await apiService.fetchWallpaper();
      if (blob) {
        next = URL.createObjectURL(blob);
      }
    } catch {
      next = null;
    }
    if (wallpaperUrlRef.current) {
      URL.revokeObjectURL(wallpaperUrlRef.current);
    }
    wallpaperUrlRef.current = next;
    setWallpaperUrl(next);
  };

  const stripShareQuery = () => {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('share')) {
        return;
      }
      url.searchParams.delete('share');
      const next = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : '') + url.hash;
      window.history.replaceState({}, '', next || '/');
    } catch {
      /* ignore */
    }
  };

  const tryLaunchShareUpload = async (): Promise<boolean> => {
    if (shareBusyRef.current) {
      return false;
    }
    shareBusyRef.current = true;
    try {
      const files = await takeShareInbox();
      stripShareQuery();
      if (!files.length) {
        return false;
      }
      return openShareRef.current(files);
    } finally {
      shareBusyRef.current = false;
    }
  };

  const enterDesktop = async (user: SessionUser) => {
    localStorageService.saveSessionUser(user);
    setSessionUser(user);
    void loadWallpaper();
    desktopReadyRef.current = true;
    const shared = await tryLaunchShareUpload();
    if (!shared) {
      window.setTimeout(() => desktopControll.launchFileBrowser(), 1100);
    }
  };

  /** 進桌面：有 Session Token 就 who-am-i；分享 inbox 喺 Gate 前唔清。 */
  const checkTokenIsValidOnStart = async () => {
    const goToGate = () => {
      localStorageService.clearTokens();
      navigate('/gate');
    };
    try {
      if (!localStorageService.hasToken()) {
        navigate('/gate');
        return;
      }

      const raw = localStorageService.getSessionToken().getFullString();
      let expired = false;
      try {
        expired = new tokenObject(raw).getRemainingMs() <= 0;
      } catch {
        expired = true;
      }
      if (expired) {
        goToGate();
        return;
      }

      try {
        const who = await apiService.whoAmI();
        if (who.status === 200 && who.data?.success && who.data.data) {
          await enterDesktop(who.data.data);
          return;
        }
      } catch (err) {
        const status = (err as AxiosError | undefined)?.response?.status;
        if (status === 401 || status === 403) {
          try {
            await apiService.renewSession();
            const user = localStorageService.getSessionUser();
            if (user && localStorageService.hasToken()) {
              await enterDesktop(user);
              return;
            }
          } catch {
            /* fall through */
          }
        } else {
          // Offline / timeout: keep cached login if possible.
          const cachedUser = localStorageService.getSessionUser();
          if (localStorageService.hasToken() && cachedUser) {
            await enterDesktop(cachedUser);
            return;
          }
        }
      }
      goToGate();
    } catch (error) {
      console.error(error);
      goToGate();
    } finally {
      setSplashScreen(false);
    }
  };

  const findMaxZindex = (tasks: TaskObj[] = taskList) => {
    if (!tasks.length) {
      return 0;
    }
    return tasks.reduce((m, t) => Math.max(m, t.zindex || 0), 0);
  };

  const readAndUpdateDisplaySize = () => {
    setScreenWidth(document.documentElement.clientWidth);
    setScreenHeight(document.documentElement.clientHeight);
  }

  return (
    <>
      <Splash showing={splashScreen} />
      <desktopAppTaskContex.Provider value={[desktopControll]}>
        <div
          id="desktop"
          style={wallpaperUrl ? { backgroundImage: `url("${wallpaperUrl}")` } : undefined}
          onClick={(event) => { if (event.target === event.currentTarget) { } }}
        >
          <FileBrowserLaunchFx play={launchFX} onDone={() => setLaunchFX(false)} />
          <Taskbar></Taskbar>
          {
            taskList && taskList.map((task: TaskObj) =>
              <Window task={task} key={task.taskID} />
            )
          }
        </div>
      </desktopAppTaskContex.Provider>
    </>
  )
}
